import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText,
  Save,
  Trash2,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  CheckCircle,
  Loader2,
  Plus,
  ArrowUp,
  ArrowDown,
  Sparkles,
  ListOrdered,
} from 'lucide-react';
import api from '../../api';
import toast from 'react-hot-toast';

/**
 * Per-client-tag SOP format admin (Admin > Workflow Tools > SOP Formats).
 *
 * One SOP format per client tag. When a project is created under a client
 * tag that has an active format, every SOP generated for that project
 * follows the client-approved structure automatically — section list,
 * registry locations, table layouts, and formatting conventions. Tags with
 * no format use the standard Jarvis 12-section structure.
 *
 * Mirrors ClientTagBrandingAdmin's expandable-card pattern.
 */

interface ClientTag {
  tag_id: number;
  tag_name: string;
  tag_type: 'client' | 'internal' | 'rnd';
  active: boolean;
  display_order: number;
}

type SectionRole = 'standard' | 'procedure' | 'controls_registry' | 'gaps_register';

interface FormatSection {
  section_number: string;
  section_title: string;
  role: SectionRole;
  columns?: string[];
  guidance?: string;
}

interface SopFormat {
  id: number;
  client_tag_id: number;
  format_name: string;
  description: string | null;
  sections: FormatSection[];
  format_rules: string | null;
  options?: Record<string, unknown>;
  active: boolean;
  updated_at: string;
}

interface FormatPreset {
  key: string;
  formatName: string;
  description: string;
  sections: FormatSection[];
  formatRules: string;
  options?: Record<string, unknown>;
}

interface FormatForm {
  formatName: string;
  description: string;
  formatRules: string;
  sections: FormatSection[];
  active: boolean;
  // Pipeline behaviour switches (e.g. spaced Section 5 bullets). Not edited
  // in the UI — carried through from presets/saved rows so saving never
  // drops them.
  options?: Record<string, unknown>;
}

const EMPTY_FORM: FormatForm = {
  formatName: '',
  description: '',
  formatRules: '',
  sections: [],
  active: true,
};

const ROLE_LABELS: Record<SectionRole, string> = {
  standard: 'Standard',
  procedure: 'Procedure (AI Call 2)',
  controls_registry: 'Controls Registry',
  gaps_register: 'Gaps Register',
};

const ClientSopFormatsAdmin: React.FC = () => {
  const [tags, setTags] = useState<ClientTag[]>([]);
  const [formatByTag, setFormatByTag] = useState<Record<number, SopFormat | null>>({});
  const [presets, setPresets] = useState<FormatPreset[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedTag, setExpandedTag] = useState<number | null>(null);
  const [forms, setForms] = useState<Record<number, FormatForm>>({});
  const [saving, setSaving] = useState<number | null>(null);
  const [deleting, setDeleting] = useState<number | null>(null);

  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      const [tagsRes, presetsRes] = await Promise.all([
        api.get('/api/client-tags'),
        api.get('/api/client-tags/sop-format-presets').catch(() => null),
      ]);
      const fresh: ClientTag[] = Array.isArray(tagsRes?.data?.tags) ? tagsRes.data.tags : [];
      fresh.sort((a, b) => {
        const typeOrder = { client: 1, internal: 2, rnd: 3 } as const;
        if (typeOrder[a.tag_type] !== typeOrder[b.tag_type]) {
          return typeOrder[a.tag_type] - typeOrder[b.tag_type];
        }
        return a.tag_name.localeCompare(b.tag_name);
      });
      setTags(fresh);
      setPresets(Array.isArray(presetsRes?.data?.presets) ? presetsRes.data.presets : []);

      const map: Record<number, SopFormat | null> = {};
      await Promise.all(
        fresh.map(async (t) => {
          try {
            const r = await api.get(`/api/client-tags/${t.tag_id}/sop-format`);
            map[t.tag_id] = r?.data?.success && r?.data?.data ? r.data.data : null;
          } catch (_) {
            map[t.tag_id] = null;
          }
        })
      );
      setFormatByTag(map);
    } catch (err: any) {
      toast.error('Failed to load client tags');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const formFromFormat = (fmt: SopFormat): FormatForm => ({
    formatName: fmt.format_name || '',
    description: fmt.description || '',
    formatRules: fmt.format_rules || '',
    sections: Array.isArray(fmt.sections) ? fmt.sections.map((s) => ({ ...s })) : [],
    active: fmt.active !== false,
    options: fmt.options,
  });

  const ensureForm = (tag: ClientTag) => {
    setForms((prev) => {
      if (prev[tag.tag_id]) return prev;
      const fmt = formatByTag[tag.tag_id];
      return {
        ...prev,
        [tag.tag_id]: fmt ? formFromFormat(fmt) : { ...EMPTY_FORM },
      };
    });
  };

  const toggleExpand = (tag: ClientTag) => {
    if (expandedTag === tag.tag_id) {
      setExpandedTag(null);
      return;
    }
    ensureForm(tag);
    setExpandedTag(tag.tag_id);
  };

  const patchForm = (tagId: number, patch: Partial<FormatForm>) => {
    setForms((prev) => ({ ...prev, [tagId]: { ...(prev[tagId] || EMPTY_FORM), ...patch } }));
  };

  const patchSection = (tagId: number, idx: number, patch: Partial<FormatSection>) => {
    setForms((prev) => {
      const form = prev[tagId] || EMPTY_FORM;
      const sections = form.sections.map((s, i) => (i === idx ? { ...s, ...patch } : s));
      return { ...prev, [tagId]: { ...form, sections } };
    });
  };

  const moveSection = (tagId: number, idx: number, dir: -1 | 1) => {
    setForms((prev) => {
      const form = prev[tagId] || EMPTY_FORM;
      const target = idx + dir;
      if (target < 0 || target >= form.sections.length) return prev;
      const sections = [...form.sections];
      [sections[idx], sections[target]] = [sections[target], sections[idx]];
      return { ...prev, [tagId]: { ...form, sections } };
    });
  };

  const removeSection = (tagId: number, idx: number) => {
    setForms((prev) => {
      const form = prev[tagId] || EMPTY_FORM;
      return { ...prev, [tagId]: { ...form, sections: form.sections.filter((_, i) => i !== idx) } };
    });
  };

  const addSection = (tagId: number) => {
    setForms((prev) => {
      const form = prev[tagId] || EMPTY_FORM;
      const nextNum = `${form.sections.length + 1}.0`;
      return {
        ...prev,
        [tagId]: {
          ...form,
          sections: [
            ...form.sections,
            { section_number: nextNum, section_title: '', role: 'standard', guidance: '' },
          ],
        },
      };
    });
  };

  const applyPreset = (tagId: number, preset: FormatPreset) => {
    patchForm(tagId, {
      formatName: preset.formatName,
      description: preset.description,
      formatRules: preset.formatRules,
      sections: preset.sections.map((s) => ({ ...s })),
      active: true,
      options: preset.options,
    });
    toast.success(`Loaded "${preset.formatName}" — review and save to apply`);
  };

  const validateForm = (form: FormatForm): string | null => {
    if (!form.formatName.trim()) return 'Format name is required';
    if (form.sections.length === 0) return 'At least one section is required';
    const procedures = form.sections.filter((s) => s.role === 'procedure');
    if (procedures.length !== 1) return 'Exactly one section must have the Procedure role';
    if (procedures[0].section_number !== '5.0')
      return 'The Procedure section must be numbered 5.0 (the generation pipeline produces Section 5 in a dedicated call)';
    if (form.sections.filter((s) => s.role === 'controls_registry').length > 1)
      return 'At most one Controls Registry section';
    if (form.sections.filter((s) => s.role === 'gaps_register').length > 1)
      return 'At most one Gaps Register section';
    const nums = form.sections.map((s) => s.section_number);
    if (new Set(nums).size !== nums.length) return 'Section numbers must be unique';
    if (form.sections.some((s) => !s.section_title.trim())) return 'Every section needs a title';
    return null;
  };

  const handleSave = async (tag: ClientTag) => {
    const form = forms[tag.tag_id];
    if (!form) return;
    const error = validateForm(form);
    if (error) {
      toast.error(error);
      return;
    }
    try {
      setSaving(tag.tag_id);
      const payload = {
        formatName: form.formatName.trim(),
        description: form.description.trim() || null,
        formatRules: form.formatRules.trim() || null,
        sections: form.sections.map((s) => ({
          section_number: s.section_number.trim(),
          section_title: s.section_title.trim(),
          role: s.role,
          ...(s.columns && s.columns.length > 0 ? { columns: s.columns } : {}),
          ...(s.guidance && s.guidance.trim() ? { guidance: s.guidance.trim() } : {}),
        })),
        active: form.active,
        ...(form.options ? { options: form.options } : {}),
      };
      const res = await api.post(`/api/client-tags/${tag.tag_id}/sop-format`, payload);
      if (res?.data?.success) {
        toast.success(`Saved SOP format for ${tag.tag_name}`);
        await loadAll();
      } else {
        const details = Array.isArray(res?.data?.details) ? `: ${res.data.details[0]}` : '';
        toast.error((res?.data?.error || 'Save failed') + details);
      }
    } catch (err: any) {
      const details = Array.isArray(err?.response?.data?.details)
        ? `: ${err.response.data.details[0]}`
        : '';
      toast.error((err?.response?.data?.error || err?.message || 'Failed to save SOP format') + details);
    } finally {
      setSaving(null);
    }
  };

  const handleDelete = async (tag: ClientTag) => {
    if (
      !window.confirm(
        `Remove the SOP format for "${tag.tag_name}"? SOPs generated for this client will revert to the standard Jarvis structure.`
      )
    ) {
      return;
    }
    try {
      setDeleting(tag.tag_id);
      const res = await api.delete(`/api/client-tags/${tag.tag_id}/sop-format`);
      if (res?.data?.success) {
        toast.success(`Removed SOP format for ${tag.tag_name}`);
        setForms((prev) => {
          const copy = { ...prev };
          delete copy[tag.tag_id];
          return copy;
        });
        await loadAll();
      } else {
        toast.error(res?.data?.error || 'Delete failed');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.message || 'Failed to delete SOP format');
    } finally {
      setDeleting(null);
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center text-gray-500">
        <Loader2 className="w-6 h-6 animate-spin inline mr-2" />
        Loading client tags…
      </div>
    );
  }

  const configuredCount = Object.values(formatByTag).filter(Boolean).length;

  return (
    <div className="space-y-4">
      {/* Summary header */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
              <FileText className="w-4 h-4 text-blue-600" />
              Client SOP Formats
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              One SOP format per client tag. Applied automatically to every SOP generated for
              projects created under that Client Engagement tag.
            </p>
          </div>
          <div className="text-xs text-gray-600 flex gap-3">
            <span className="flex items-center gap-1">
              <CheckCircle className="w-3.5 h-3.5 text-green-600" />
              {configuredCount} configured
            </span>
            <span className="flex items-center gap-1">
              <AlertCircle className="w-3.5 h-3.5 text-gray-400" />
              {tags.length - configuredCount} using standard format
            </span>
          </div>
        </div>
      </div>

      {tags.map((tag) => {
        const fmt = formatByTag[tag.tag_id];
        const form = forms[tag.tag_id];
        const isExpanded = expandedTag === tag.tag_id;
        const isSaving = saving === tag.tag_id;
        const isDeleting = deleting === tag.tag_id;
        const dotColor =
          tag.tag_type === 'client' ? 'bg-blue-500' :
          tag.tag_type === 'internal' ? 'bg-purple-500' : 'bg-amber-500';

        return (
          <div key={tag.tag_id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <button
              type="button"
              onClick={() => toggleExpand(tag)}
              className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors text-left"
            >
              <div className="flex items-center gap-3">
                <span className={`inline-block w-2 h-2 rounded-full ${dotColor}`} />
                <div>
                  <div className="font-medium text-gray-900 text-sm flex items-center gap-2">
                    {tag.tag_name}
                    <span className="text-xs text-gray-500 font-normal">
                      {tag.tag_type === 'client' ? 'Client' : tag.tag_type === 'internal' ? 'Internal' : 'R&D / Learning'}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {fmt ? (
                      <span className="flex items-center gap-1.5">
                        <ListOrdered className="w-3 h-3 text-blue-500" />
                        {fmt.format_name}
                        <span className="text-gray-400">· {Array.isArray(fmt.sections) ? fmt.sections.length : 0} sections</span>
                        {!fmt.active && <span className="text-amber-700">· inactive</span>}
                      </span>
                    ) : (
                      <span className="text-gray-500">Standard Jarvis format (no client format configured)</span>
                    )}
                  </div>
                </div>
              </div>
              {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
            </button>

            {isExpanded && form && (
              <div className="border-t border-gray-100 p-4 space-y-4 bg-gray-50">
                {/* Presets */}
                {presets.length > 0 && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-gray-500">Start from:</span>
                    {presets.map((p) => (
                      <button
                        key={p.key}
                        type="button"
                        onClick={() => applyPreset(tag.tag_id, p)}
                        className="px-2.5 py-1 text-xs font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-md flex items-center gap-1"
                      >
                        <Sparkles className="w-3 h-3" />
                        {p.formatName}
                      </button>
                    ))}
                  </div>
                )}

                {/* Name + active */}
                <div className="flex items-end gap-3">
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-gray-700 mb-1">Format name</label>
                    <input
                      type="text"
                      value={form.formatName}
                      onChange={(e) => patchForm(tag.tag_id, { formatName: e.target.value })}
                      placeholder="e.g. Pacific Life SOP Format v2"
                      className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <label className="flex items-center gap-1.5 text-xs text-gray-700 pb-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.active}
                      onChange={(e) => patchForm(tag.tag_id, { active: e.target.checked })}
                      className="rounded border-gray-300"
                    />
                    Active (applied at generation)
                  </label>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={form.description}
                    onChange={(e) => patchForm(tag.tag_id, { description: e.target.value })}
                    rows={2}
                    placeholder="Where this format came from and what it enforces"
                    className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Sections */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-xs font-medium text-gray-700">
                      Sections <span className="text-gray-400 font-normal">(exactly one Procedure section, numbered 5.0)</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => addSection(tag.tag_id)}
                      className="px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-md flex items-center gap-1"
                    >
                      <Plus className="w-3 h-3" />
                      Add section
                    </button>
                  </div>
                  <div className="space-y-2">
                    {form.sections.map((s, idx) => (
                      <div key={idx} className="bg-white border border-gray-200 rounded-md p-3 space-y-2">
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={s.section_number}
                            onChange={(e) => patchSection(tag.tag_id, idx, { section_number: e.target.value })}
                            className="w-16 px-2 py-1 text-xs font-mono border border-gray-300 rounded-md"
                            placeholder="1.0"
                          />
                          <input
                            type="text"
                            value={s.section_title}
                            onChange={(e) => patchSection(tag.tag_id, idx, { section_title: e.target.value })}
                            className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded-md"
                            placeholder="Section title"
                          />
                          <select
                            value={s.role}
                            onChange={(e) => patchSection(tag.tag_id, idx, { role: e.target.value as SectionRole })}
                            className="px-2 py-1 text-xs border border-gray-300 rounded-md bg-white"
                          >
                            {(Object.keys(ROLE_LABELS) as SectionRole[]).map((r) => (
                              <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                            ))}
                          </select>
                          <button type="button" onClick={() => moveSection(tag.tag_id, idx, -1)} disabled={idx === 0} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30">
                            <ArrowUp className="w-3.5 h-3.5" />
                          </button>
                          <button type="button" onClick={() => moveSection(tag.tag_id, idx, 1)} disabled={idx === form.sections.length - 1} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30">
                            <ArrowDown className="w-3.5 h-3.5" />
                          </button>
                          <button type="button" onClick={() => removeSection(tag.tag_id, idx)} className="p-1 text-red-400 hover:text-red-600">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <input
                          type="text"
                          value={(s.columns || []).join(' | ')}
                          onChange={(e) => {
                            const raw = e.target.value;
                            patchSection(tag.tag_id, idx, {
                              columns: raw.trim() ? raw.split('|').map((c) => c.trim()).filter(Boolean) : undefined,
                            });
                          }}
                          className="w-full px-2 py-1 text-xs font-mono border border-gray-200 rounded-md text-gray-600"
                          placeholder="Table columns, pipe-separated (leave blank for prose) — e.g. Control ID | Description | Owner | Type | Frequency"
                        />
                        <textarea
                          value={s.guidance || ''}
                          onChange={(e) => patchSection(tag.tag_id, idx, { guidance: e.target.value })}
                          rows={2}
                          className="w-full px-2 py-1 text-xs border border-gray-200 rounded-md text-gray-600"
                          placeholder="Generation guidance for this section (what it must contain, sub-headings, entry format...)"
                        />
                      </div>
                    ))}
                    {form.sections.length === 0 && (
                      <div className="text-xs text-gray-400 border border-dashed border-gray-300 rounded-md p-4 text-center">
                        No sections defined — add sections or load a preset above.
                      </div>
                    )}
                  </div>
                </div>

                {/* Global format rules */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Global format rules <span className="text-gray-400 font-normal">(injected into both generation calls)</span>
                  </label>
                  <textarea
                    value={form.formatRules}
                    onChange={(e) => patchForm(tag.tag_id, { formatRules: e.target.value })}
                    rows={5}
                    className="w-full px-3 py-1.5 text-xs font-mono border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    placeholder={'- One rule per line, e.g.\n- Cross-reference controls inline by ID; define each control once in the Controls Registry.'}
                  />
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                  {fmt ? (
                    <button
                      type="button"
                      onClick={() => handleDelete(tag)}
                      disabled={isDeleting || isSaving}
                      className="px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-50 rounded-md flex items-center gap-1.5"
                    >
                      {isDeleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                      Remove format
                    </button>
                  ) : <span />}
                  <button
                    type="button"
                    onClick={() => handleSave(tag)}
                    disabled={isSaving || !form.formatName.trim()}
                    className="px-4 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-md flex items-center gap-1.5"
                  >
                    {isSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                    {fmt ? 'Save changes' : 'Save format'}
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ClientSopFormatsAdmin;
